<template>
    <ion-card v-if="initialItem" class="order-action">
        <ion-card class="mx-0 mt-0 size-card" :class="{ 'd-none': hideSizeDisplay }">
            <ion-card-content class="topping-card-content ml-8">
                <chooseSize :menuItem="initialItem" @size="size = $event" @crust="crust = $event" />
            </ion-card-content>
        </ion-card>

        <ion-card class="mx-0 my-0 flex-fill-y">
            <toppingFactory
                :initialItem="initialItem"
                :availableTopping="availableTopping"
                :selectedSize="size"
                :selectedRecipe="crust"
                :sauceCups="sauceCups"
                class="topping-factory topping-card-content"
                v-model:flavor="flavor"
                v-model:dressing="dressings"
                v-model:toppings="toppings"
                v-model:defaultToppings="defaultToppings"
                v-model:twoSidedItem="twoSidedItem"
                @blockSubmit="$emit('blockSubmit', $event)"
                @limit="maxLimit"
                checkCount
            />
            <instructionModal v-if="!initialItem.isAddOn" :instructions="instructions" @update="$emit('instructions', $event)" />
            <ion-item lines="full" data-cy="menu-item-quantity" v-if="quantity != undefined && maxQuantity > 1">
                <quantity v-model="quantity" :limit="maxQuantity" :lowerLimit="1" should-show-error-toast />
            </ion-item>
            <ion-row class="ion-text-center ion-justify-content-center py-8 ion-padding-horizontal">
                <ion-col size="6" class="py-0" v-if="priceAndCal">
                    <p data-cy="price-calories">
                        <span v-if="!hidePrice">${{ priceAndCal.itemPrice }}</span>
                        <span v-if="!hidePrice && priceAndCal.calories"> | </span>
                        <span v-if="priceAndCal.calories">{{ priceAndCal.calories }} </span>
                    </p>
                    {{ initialItem.isSalad ? 'Calories do not include dressing.' : '' }}
                </ion-col>
                <ion-col size="12" class="pt-0" data-cy="calorie-description">
                    <div v-if="!isGiftCard">2,000 calories per day is used for general nutrition advice, but calorie needs may vary. Additional nutrition information is available upon request.</div>
                </ion-col>
                <ion-col size="12" class="pt-2">
                    <div v-html="initialItem.itemAdditionalNutritionDescription"></div>
                </ion-col>
            </ion-row>
        </ion-card>
    </ion-card>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch, computed } from 'vue';
import { IonCard, IonCardContent, IonRow, IonCol, IonItem } from '@ionic/vue';
import { MenuItem, ToppingReq, Instruction, AvailableTopping, ToppingPrice, RequiredTopping, Topping } from '@/models/menuModels';
import chooseSize from '@/components/menu/chooseSize.vue';
import quantity from '@/components/inputs/quantity.vue';
import toppingFactory from '@/components/menu/toppings/toppingFactory.vue';
import instructionModal from '@/components/menu/toppings/instructionModal.vue';
import { fetchToppings, lineItemMapper, itemPrice } from '@/services/orderService';
import { LineItem } from '@/models/cartModels';
import { BAKERY_PIZZA_TOPPING_LIMIT, CALZATO_TOPPING_LIMIT, PIZZA_TOPPING_LIMIT } from '@/constants';
import { useStore } from 'vuex';
export default defineComponent({
    props: {
        initialItem: { type: Object as PropType<MenuItem>, required: true },
        instructions: { type: Array as PropType<Instruction[]> },
        sauceCups: { type: Array as PropType<RequiredTopping[]> },
        editLineItem: { type: Object as PropType<LineItem> | undefined },
        toppingPrices: { type: Array as PropType<ToppingPrice[]> },
        hidePrice: { type: Boolean, default: false },
        maxQuantityCount: { type: Number, default: null },
        alwaysShowSize: { type: Boolean, default: false },
    },
    components: {
        IonCard,
        IonCardContent,
        IonRow,
        IonCol,
        chooseSize,
        quantity,
        toppingFactory,
        instructionModal,
        IonItem,
    },
    setup(props, { emit }) {
        const store = useStore();
        const size = ref();
        const crust = ref();
        const quantity = ref(props.editLineItem?.quantity ?? 1);
        const availableTopping = ref(props.editLineItem?.asAvailableToppings);
        const flavor = ref(props.editLineItem?.flatFlavor);
        const dressings = ref(props.editLineItem?.selectedDressings);
        const toppings = ref();
        const defaultToppings = ref(props.editLineItem?.toppings);
        const twoSidedItem = ref(props.editLineItem?.isTwoSidedCustom ? props.editLineItem : undefined);
        const maxQuantity = ref(props.maxQuantityCount ?? 10);

        const hideSizeDisplay = computed(() => {
            if (props.alwaysShowSize) {
                return false;
            }

            return props.initialItem.isOneSize && props.initialItem.isOneRecipe;
        });

        const isGiftCard = computed(() => props.initialItem.isGiftCard);

        const priceAndCal = ref();
        /** All child components roll up into this LineItem, Two side items copy some info from here */
        const lineItem = computed(() => {
            if (props.initialItem.isSub || props.initialItem.isCalzato || props.initialItem.isPizza) {
                emit('blockSubmit', !crust.value || !toppings.value);
                if (!crust.value || !toppings.value) return;
            }
            return lineItemMapper(
                props.initialItem,
                props.instructions ?? [],
                quantity.value,
                //@ts-ignore
                new AvailableTopping({
                    flavors: !flavor.value ? [] : flavor.value instanceof Array ? flavor.value : [flavor.value],
                    dressings: !dressings.value ? [] : dressings.value instanceof Array ? dressings.value : [dressings.value],
                    toppings: availableTopping.value?.toppings ?? [],
                    updatingSelectedToppings: toppings.value?.concat(defaultToppings.value ?? [])?.filter((t) => t.selected ?? []),
                }),
                crust.value,
                size.value
            );
        });
        const updateToppings = () => {
            if (!size.value || !crust.value) return;
            store.dispatch('flavorLoader', false);
            fetchToppings(
                new ToppingReq({
                    sizeId: size.value.sizeId,
                    recipeId: crust.value.recipeId,
                    itemId: props.initialItem?.itemId ?? '',
                    categoryId: props.initialItem?.categoryId ?? '',
                })
            )
                .then((t) => {
                    store.dispatch('flavorLoader', true);
                    // Set default values for selected and multiplier on wing dressings
                    // only actually selected dressing gets passed to the LineItem,  not based on dressing.selected
                    if (props.initialItem?.isWing) {
                        if (top?.location.pathname.startsWith('/pizza-pals') || top?.location.pathname.startsWith('/deals')) {
                            const matchingFlavor = t.flavors.findIndex((f) => f.name === 'Hot Honey');
                            if (matchingFlavor) {
                                t.flavors.splice(matchingFlavor, 1);
                            }
                        }

                        t.dressings.forEach((dressing) => {
                            dressing.selected = true;
                            dressing.multiplier = 1;
                        });
                    }
                    if (props.editLineItem) {
                        // aligns just fetched toppings with previously selected default toppings
                        defaultToppings.value?.forEach((defaultTop) => {
                            const preSelected = props.editLineItem?.selectedToppings.find((selected) => selected.toppingPid === defaultTop.toppingPid);
                            const notSelected = props.editLineItem?.toppings.find((t) => t.toppingPid === defaultTop.toppingPid);
                            defaultTop.selected = !!preSelected;
                            defaultTop.multiplier = preSelected?.multiplier ?? 0;
                            defaultTop.placement = preSelected?.placement ?? 3;
                            defaultTop.id = preSelected?.id ?? notSelected?.id ?? '';
                        });
                    }

                    availableTopping.value = t;

                    // limiting this to salads, where we are currently see an issue 10/4/2022 - Sean N.
                    // user selected toppings were not making it through to the topping modal on edit.
                    if (defaultToppings.value && lineItem.value?.cartName.toLowerCase().includes('salad')) {
                        toppings.value.forEach(
                            // @ts-ignore
                            (topping) => (topping.selected = defaultToppings.value.find((top) => top.id == topping.id)?.selected ?? false)
                        );
                    }
                })
                .catch(() => availableTopping.value);
        };
        watch(props.initialItem, () => updateToppings());
        watch(size, () => updateToppings());
        watch(crust, () => updateToppings());
        updateToppings();

        watch(quantity, (q) => {
            emit('quantityChange', q);
        });

        watch(twoSidedItem, async (item) => {
            if (!item) emit('twoSidedItemUpdate', undefined);
            emit('invalidToppings', false);
            if (!item || !lineItem.value) return;
            priceAndCal.value = await itemPrice(lineItem.value);
            const rightSide = await itemPrice(item);
            if (item) {
                item.price = +priceAndCal.value?.itemPrice;
                item.price2 = +rightSide?.itemPrice;
            }
            emit('twoSidedItemUpdate', item);
        });

        watch(lineItem, async (l) => {
            emit('blockSubmit', true);
            if (!lineItem.value || !l || l.recipes.length == 0) return;
            priceAndCal.value = await itemPrice(lineItem.value);
            if (l) l.price = +priceAndCal.value?.itemPrice;

            // block update of salad if dressings got dropped,
            // selectedDressings will always contain all dressing options
            // with each dressing in array containing a `selected` bool
            const hasdressings = l.selectedDressings.filter((d) => d.selected).length > 0 && lineItem.value.selectedDressings.filter((d) => d.selected).length > 0;
            if (l.isSalad && !hasdressings) return;
            emit('update', l);

            // block update of wing if no flavors selected
            const hasFlavors = l.selectedFlavors.filter((f) => f.selected).length > 0 && lineItem.value.selectedFlavors.filter((f) => f.selected).length > 0;
            if (l.isWing && !hasFlavors) return;

            setTimeout(() => emit('blockSubmit', false), 1000);
        });

        watch(lineItem, async (l) => {
            if (props.initialItem.isPizza || props.initialItem.isBakery || props.initialItem.isCalzato) {
                emit('invalidToppings', !pizzaHasFullCoverage(l?.selectedToppings) || isToppingLimitReached(l?.selectedToppings));
            } else {
                emit('invalidToppings', isToppingLimitReached(l?.selectedToppings));
            }
        });

        const pizzaHasFullCoverage = (toppings: Topping[] | undefined): boolean => {
            if (!toppings?.length) {
                return false;
            }

            const toppingPlacements = toppings.map((t) => Number(t.placement));

            if (toppingPlacements.includes(3)) {
                return true;
            }

            return toppingPlacements.includes(1) && toppingPlacements.includes(2);
        };

        const isToppingLimitReached = (toppings: Topping[] | undefined) => {
            let setLimit = PIZZA_TOPPING_LIMIT;
            if (props.initialItem?.isCalzato) {
                setLimit = CALZATO_TOPPING_LIMIT;
            } else if (props.initialItem?.isBakery) {
                setLimit = BAKERY_PIZZA_TOPPING_LIMIT;
            }
            const limit = setLimit;
            if (!toppings || toppings?.length == 0) return false;
            const count = toppings.reduce((total, topping) => (total += topping.multiplier), 0);
            return count > limit;
        };
        const maxLimit = (e) => {
            emit('limit', e);
        };

        return {
            size,
            crust,
            availableTopping,
            quantity,
            flavor,
            dressings,
            toppings,
            defaultToppings,
            lineItem,
            twoSidedItem,
            priceAndCal,
            isGiftCard,
            maxLimit,
            maxQuantity,
            hideSizeDisplay,
        };
    },
});
</script>

<style scoped>
.order-action {
    display: flex;
    flex-direction: column;
}

.topping-factory {
    flex: 0.5;
}

@media screen and (max-width: 960px) {
    .topping-card-content {
        padding-inline: 0;
    }
}
</style>
